/**
 * Contacts (tidaltech/koi#8359): people at customers, and Tidal staff routed
 * like them, attached at site, region or customer level for different
 * purposes. Backed by leatherback's Contacts service, the system of record.
 */
import type { Site } from './inventory';

export const contactPurposes = [
  'OPERATIONS',
  'RECEIVING',
  'RETURNS',
  'TECHNICAL',
  'BILLING',
  'COMMERCIAL',
  'ESCALATION',
  'CUSTOMER_SUCCESS',
  'FIELD_SERVICES',
] as const;
export type ContactPurpose = (typeof contactPurposes)[number];

export const contactPurposeLabels: Record<ContactPurpose, string> = {
  OPERATIONS: 'Operations',
  RECEIVING: 'Receiving',
  RETURNS: 'Returns',
  TECHNICAL: 'Technical',
  BILLING: 'Billing',
  COMMERCIAL: 'Commercial',
  ESCALATION: 'Escalation',
  CUSTOMER_SUCCESS: 'Customer Success',
  FIELD_SERVICES: 'Field Services',
};

/** Purposes Tidal's own people cover; the rest are the customer's. */
export const internalPurposes: readonly ContactPurpose[] = ['CUSTOMER_SUCCESS', 'FIELD_SERVICES'];

export const contactLevels = ['SITE', 'REGION', 'CUSTOMER'] as const;
export type ContactLevel = (typeof contactLevels)[number];

export const contactLevelLabels: Record<ContactLevel, string> = {
  SITE: 'Site',
  REGION: 'Region',
  CUSTOMER: 'Customer',
};

export interface ContactScope {
  level: ContactLevel;
  siteId?: string | undefined;
  country?: string | undefined;
  region?: string | undefined;
  purposes: ContactPurpose[];
  primary?: boolean | undefined;
}

export interface ContactAddress {
  line1?: string | undefined;
  line2?: string | undefined;
  postcode?: string | undefined;
  city?: string | undefined;
}

export interface Contact {
  id: string;
  customerId?: string | undefined;
  accountId?: string | undefined;
  firstName?: string | undefined;
  lastName?: string | undefined;
  email?: string | undefined;
  phone?: string | undefined;
  title?: string | undefined;
  role?: string | undefined;
  userId?: string | undefined;
  language?: string | undefined;
  notes?: string | undefined;
  inactive?: boolean | undefined;
  address?: ContactAddress | undefined;
  scopes: ContactScope[];
  version: number;
  lastModified?: string | undefined;
  lastModifiedBy?: string | undefined;
}

export type ContactInput = Omit<Contact, 'id' | 'version' | 'lastModified' | 'lastModifiedBy'>;

export const clearableContactFields = [
  'email',
  'phone',
  'title',
  'role',
  'notes',
  'language',
  'address',
  'scopes',
] as const;
export type ClearableContactField = (typeof clearableContactFields)[number];

export interface ContactPatch extends Partial<ContactInput> {
  id: string;
  version: number;
  clear?: ClearableContactField[] | undefined;
}

export interface ContactResolution {
  purpose: ContactPurpose;
  /** Where the contacts were found; undefined when nobody is set. */
  level?: ContactLevel | undefined;
  contacts: Contact[];
}

/** Languages offered for messages; the backend accepts any BCP 47 code. */
export const contactLanguages: { code: string; label: string }[] = [
  { code: 'nb', label: 'Norwegian' },
  { code: 'en', label: 'English' },
  { code: 'es', label: 'Spanish' },
  { code: 'is', label: 'Icelandic' },
  { code: 'fo', label: 'Faroese' },
];

export function contactName(contact: Pick<Contact, 'firstName' | 'lastName'>): string {
  return [contact.firstName, contact.lastName]
    .map((part) => part?.trim())
    .filter(Boolean)
    .join(' ');
}

export function isStaffContact(contact: Pick<Contact, 'userId'>): boolean {
  return Boolean(contact.userId);
}

/**
 * Country names site labels use today (free text) as ISO 3166-1 alpha-2
 * codes, as leatherback reads them. Scotland is GB: ISO has no code of its
 * own for it, and the region name keeps its regions apart. A typed
 * Site.country (tidaltech/koi#8360) replaces this.
 */
const countryCodes: Record<string, string> = {
  norway: 'NO',
  norge: 'NO',
  scotland: 'GB',
  'united kingdom': 'GB',
  uk: 'GB',
  'great britain': 'GB',
  england: 'GB',
  ireland: 'IE',
  chile: 'CL',
  iceland: 'IS',
  'faroe islands': 'FO',
  'faroe is.': 'FO',
  færøyene: 'FO',
  australia: 'AU',
  canada: 'CA',
};

/** A country as an ISO 3166-1 alpha-2 code; empty when it's neither a code nor a known name. */
export function countryCode(country: string | undefined): string {
  const c = (country ?? '').trim();
  if (/^[a-z]{2}$/i.test(c)) return c.toUpperCase();
  return countryCodes[c.toLowerCase()] ?? '';
}

/** A site's country (code and the label's own name) and region. */
export function siteCountryRegion(site: Site): {
  country: string;
  countryName: string;
  region: string;
} {
  const countryName = site.labels?.country?.trim() ?? '';
  return {
    country: countryCode(countryName),
    countryName,
    region: site.labels?.region?.trim() ?? '',
  };
}

const same = (a: string | undefined, b: string | undefined): boolean =>
  (a ?? '').trim().toLowerCase() === (b ?? '').trim().toLowerCase();

/** The scope's level when it applies to the site (of the same customer). */
export function scopeLevelAt(scope: ContactScope, site: Site): ContactLevel | undefined {
  switch (scope.level) {
    case 'SITE':
      return scope.siteId === site.id ? 'SITE' : undefined;
    case 'REGION': {
      const { country, region } = siteCountryRegion(site);
      return region &&
        country &&
        countryCode(scope.country) === country &&
        same(scope.region, region)
        ? 'REGION'
        : undefined;
    }
    case 'CUSTOMER':
      return 'CUSTOMER';
  }
}

/**
 * Who to talk to at a site for a purpose: the active contacts at the nearest
 * level that has any (site, then region, then customer), primary first, then
 * by name. Mirrors leatherback's ResolveContacts.
 */
export function resolveContacts(
  contacts: readonly Contact[],
  site: Site,
  purpose: ContactPurpose,
): ContactResolution {
  for (const level of contactLevels) {
    const matches = contacts.flatMap((contact) => {
      if (contact.inactive || contact.customerId !== site.customerId) return [];
      const scope = contact.scopes.find(
        (candidate) =>
          scopeLevelAt(candidate, site) === level && candidate.purposes.includes(purpose),
      );
      return scope ? [{ contact, primary: Boolean(scope.primary) }] : [];
    });
    if (matches.length === 0) continue;
    matches.sort(
      (a, b) =>
        Number(b.primary) - Number(a.primary) ||
        contactName(a.contact).localeCompare(contactName(b.contact)),
    );
    return { purpose, level, contacts: matches.map((match) => match.contact) };
  }
  return { purpose, contacts: [] };
}

/** One scope in words, e.g. "Site BHM" or "Region Norway / North". */
export function scopeLabel(scope: ContactScope, sitesById: ReadonlyMap<string, Site>): string {
  switch (scope.level) {
    case 'SITE': {
      const site = scope.siteId ? sitesById.get(scope.siteId) : undefined;
      return `Site ${site?.name ?? scope.siteId ?? ''}`.trim();
    }
    case 'REGION': {
      // The country as the customer's own sites name it ("Scotland"), else its code.
      const code = countryCode(scope.country);
      const named = [...sitesById.values()]
        .map(siteCountryRegion)
        .find((s) => s.country === code && same(s.region, scope.region));
      const country = named?.countryName || scope.country;
      return `Region ${[country, scope.region].filter(Boolean).join(' / ')}`;
    }
    case 'CUSTOMER':
      return 'Whole customer';
  }
}

/** The regions (country code, its name on the sites, region) a customer's sites are in. */
export function customerRegions(
  sites: readonly Site[],
): { country: string; countryName: string; region: string }[] {
  const seen = new Map<string, { country: string; countryName: string; region: string }>();
  for (const site of sites) {
    const { country, countryName, region } = siteCountryRegion(site);
    if (!country || !region) continue;
    const key = `${country}/${region.toLowerCase()}`;
    if (!seen.has(key)) seen.set(key, { country, countryName, region });
  }
  return [...seen.values()].sort(
    (a, b) => a.countryName.localeCompare(b.countryName) || a.region.localeCompare(b.region),
  );
}

/** The default language for a contact in a country, by ISO 3166-1 alpha-2 code. */
const languageByCountry: Record<string, string> = {
  NO: 'nb',
  GB: 'en',
  IE: 'en',
  CL: 'es',
  IS: 'is',
  FO: 'fo',
  AU: 'en',
  CA: 'en',
};

/** The default language for a country, given as a code or a name. */
export function defaultLanguageForCountry(country: string | undefined): string | undefined {
  return languageByCountry[countryCode(country)];
}

// ---- The edit form ----

export interface ContactScopeDraft {
  key: string;
  level: ContactLevel;
  siteId: string;
  /** "CODE / Region" for a region scope, e.g. "NO / North". */
  regionKey: string;
  purposes: ContactPurpose[];
  primary: boolean;
}

export interface ContactDraft {
  firstName: string;
  lastName: string;
  title: string;
  role: string;
  email: string;
  phone: string;
  language: string;
  notes: string;
  staff: boolean;
  userId: string;
  inactive: boolean;
  scopes: ContactScopeDraft[];
}

let scopeKeyCounter = 0;
export function newScopeKey(): string {
  scopeKeyCounter += 1;
  return `scope-${scopeKeyCounter}`;
}

export const regionKey = (country: string, region: string): string => `${country} / ${region}`;

function splitRegionKey(key: string): { country: string; region: string } {
  const [country = '', ...rest] = key.split(' / ');
  return { country: country.trim(), region: rest.join(' / ').trim() };
}

export function contactDraft(contact?: Contact, initialScope?: ContactScope): ContactDraft {
  const scopes = contact?.scopes ?? (initialScope ? [initialScope] : []);
  return {
    firstName: contact?.firstName ?? '',
    lastName: contact?.lastName ?? '',
    title: contact?.title ?? '',
    role: contact?.role ?? '',
    email: contact?.email ?? '',
    phone: contact?.phone ?? '',
    language: contact?.language ?? '',
    notes: contact?.notes ?? '',
    staff: Boolean(contact?.userId),
    userId: contact?.userId ?? '',
    inactive: Boolean(contact?.inactive),
    scopes: scopes.map((scope) => ({
      key: newScopeKey(),
      level: scope.level,
      siteId: scope.siteId ?? '',
      regionKey:
        scope.country && scope.region
          ? regionKey(countryCode(scope.country) || scope.country, scope.region)
          : '',
      purposes: [...scope.purposes],
      primary: Boolean(scope.primary),
    })),
  };
}

export type ContactDraftErrors = Partial<Record<'name' | 'email' | 'userId', string>> & {
  scopes?: Record<string, string>;
};

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function validateContactDraft(draft: ContactDraft): ContactDraftErrors {
  const errors: ContactDraftErrors = {};
  if (!draft.firstName.trim() && !draft.lastName.trim()) {
    errors.name = 'Enter a first or last name.';
  }
  if (draft.email.trim() && !EMAIL.test(draft.email.trim())) {
    errors.email = 'Enter an email address like name@example.com.';
  }
  if (draft.staff && !EMAIL.test(draft.userId.trim())) {
    errors.userId = 'Enter the staff member’s Tidal email.';
  }
  const scopeErrors: Record<string, string> = {};
  const seen = new Set<string>();
  for (const scope of draft.scopes) {
    let identity = 'customer';
    if (scope.level === 'SITE') {
      if (!scope.siteId) scopeErrors[scope.key] = 'Choose a site.';
      identity = `site/${scope.siteId}`;
    } else if (scope.level === 'REGION') {
      if (!scope.regionKey) scopeErrors[scope.key] = 'Choose a region.';
      identity = `region/${scope.regionKey.toLowerCase()}`;
    }
    if (!scopeErrors[scope.key] && scope.purposes.length === 0) {
      scopeErrors[scope.key] = 'Choose at least one purpose.';
    }
    if (!scopeErrors[scope.key] && seen.has(identity)) {
      scopeErrors[scope.key] = 'This contact already has this one; add the purposes there.';
    }
    seen.add(identity);
  }
  if (Object.keys(scopeErrors).length > 0) errors.scopes = scopeErrors;
  return errors;
}

export function hasDraftErrors(errors: ContactDraftErrors): boolean {
  return Boolean(errors.name || errors.email || errors.userId || errors.scopes);
}

export function draftScopes(draft: ContactDraft): ContactScope[] {
  return draft.scopes.map((scope) => {
    const base = { purposes: scope.purposes, ...(scope.primary ? { primary: true } : {}) };
    if (scope.level === 'SITE') return { level: 'SITE', siteId: scope.siteId, ...base };
    if (scope.level === 'REGION')
      return { level: 'REGION', ...splitRegionKey(scope.regionKey), ...base };
    return { level: 'CUSTOMER', ...base };
  });
}

const optional = (value: string): string | undefined => value.trim() || undefined;

export function buildContactInput(draft: ContactDraft, customerId: string): ContactInput {
  return {
    customerId,
    firstName: optional(draft.firstName),
    lastName: optional(draft.lastName),
    title: optional(draft.title),
    role: optional(draft.role),
    email: optional(draft.email)?.toLowerCase(),
    phone: optional(draft.phone),
    language: optional(draft.language),
    notes: optional(draft.notes),
    userId: draft.staff ? optional(draft.userId)?.toLowerCase() : undefined,
    inactive: draft.inactive || undefined,
    scopes: draftScopes(draft),
  };
}

/** The changes from contact to draft, with cleared fields named in clear. */
export function buildContactPatch(contact: Contact, draft: ContactDraft): ContactPatch | null {
  const input = buildContactInput(draft, contact.customerId ?? '');
  const patch: ContactPatch = { id: contact.id, version: contact.version };
  const clear: ClearableContactField[] = [];
  let changed = false;
  const text = [
    'firstName',
    'lastName',
    'title',
    'role',
    'email',
    'phone',
    'language',
    'notes',
  ] as const;
  for (const field of text) {
    const next = input[field];
    const previous = contact[field] || undefined;
    if (next === previous) continue;
    changed = true;
    if (next === undefined) {
      if ((clearableContactFields as readonly string[]).includes(field)) {
        clear.push(field as ClearableContactField);
      } else {
        // first/last name: an empty string clears it on the server.
        patch[field] = '';
      }
    } else {
      patch[field] = next;
    }
  }
  if ((input.userId ?? '') !== (contact.userId ?? '')) {
    changed = true;
    patch.userId = input.userId ?? '';
  }
  if (Boolean(input.inactive) !== Boolean(contact.inactive)) {
    changed = true;
    patch.inactive = Boolean(input.inactive);
  }
  if (JSON.stringify(input.scopes) !== JSON.stringify(normalizeScopes(contact.scopes))) {
    changed = true;
    if (input.scopes.length === 0) clear.push('scopes');
    else patch.scopes = input.scopes;
  }
  if (!changed) return null;
  if (clear.length > 0) patch.clear = clear;
  return patch;
}

function normalizeScopes(scopes: readonly ContactScope[]): ContactScope[] {
  return scopes.map((scope) => {
    const base = { purposes: scope.purposes, ...(scope.primary ? { primary: true } : {}) };
    if (scope.level === 'SITE') return { level: 'SITE', siteId: scope.siteId, ...base };
    if (scope.level === 'REGION') {
      return { level: 'REGION', country: scope.country ?? '', region: scope.region ?? '', ...base };
    }
    return { level: 'CUSTOMER', ...base };
  });
}
